import { useState, useEffect, useRef } from "react";
import { router } from "@inertiajs/react";
import I from "../../components/Icons";
import ManualCorrectionDialog from "../../components/ManualCorrectionDialog";
import SpotFixDialog from "../../components/SpotFixDialog";
import OutputLightbox from "../../components/OutputLightbox";
import { modeLabel } from "../../lib/generationModes";
import { correctGenerationOutput, regenerateGenerationOutput, retryGenerationOutput, spotFixGenerationOutput } from "../../lib/generationOutputs";
import { RequestError } from "../../lib/http";
import { common, pick } from "../../lib/i18n";
import { formatTokensCompact, formatTokensPl } from "../../lib/tokens";
import useGenerationSocket from "../../hooks/useGenerationSocket";
import type { CurrentUser, FeatureFlags, GenerationOutput, Product as ProductBase } from "../../types";

type Job = {
  id: number;
  status: string;
  outputs: GenerationOutput[];
};

type Product = Pick<ProductBase, "id" | "name" | "variants_json" | "dimensions_json">;

type ReferencePhoto = { id: number; url: string };

type Props = {
  product: Product;
  job: Job;
  current_user: CurrentUser;
  reference_photos: ReferencePhoto[];
  feature_flags?: FeatureFlags;
};

const Chk = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 8.5l3.5 3.5 6.5-7"/>
  </svg>
);

function requestErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof RequestError && !error.message.startsWith("Request failed with status")) {
    return error.message;
  }
  return fallback;
}

const copy = {
  pl: {
    product: "Produkt", visuals: "Grafiki", results: "Wyniki", filesUploaded: "Pliki wgrane", ready: "gotowe",
    productStep: "PRODUKT", visualsStep: "GRAFIKI", selectedModes: "Wybrane tryby", submitted: "zlecone", step: "KROK",
    generatingVisuals: "Generowanie grafik", downloadZip: "Pobierz ZIP", readyLower: "gotowych", tokens: "tokenów",
    promptUsed: "Prompt użyty do generacji", usedTokens: (n: string) => `Zużyto ${n} tokenów`, variants: (n: number) => `${n} wariantów`,
    completed: (d: number, t: number) => `Ukończono ${d} z ${t} grafik`,
    readyOf: (d: number, t: number, r: number) => `${d} z ${t} gotowych${r > 0 ? ` · ${r} w trakcie` : "…"}`,
  },
  en: {
    product: "Product", visuals: "Visuals", results: "Results", filesUploaded: "Files uploaded", ready: "ready",
    productStep: "PRODUCT", visualsStep: "VISUALS", selectedModes: "Selected modes", submitted: "submitted", step: "STEP",
    generatingVisuals: "Generating visuals", downloadZip: "Download ZIP", readyLower: "ready", tokens: "tokens",
    promptUsed: "Prompt used for generation", usedTokens: (n: string) => `Used ${n} tokens`, variants: (n: number) => `${n} variants`,
    completed: (d: number, t: number) => `Completed ${d} of ${t} visuals`,
    readyOf: (d: number, t: number, r: number) => `${d} of ${t} ready${r > 0 ? ` · ${r} in progress` : "…"}`,
  },
};

export default function GenerationJobShow({ product, job, current_user, reference_photos, feature_flags }: Props) {
  const t = pick(copy);
  const c = common();
  const isSpotFixEnabled = feature_flags?.spot_fix_enabled ?? true;
  const canSpotFix = isSpotFixEnabled && reference_photos.length > 0;
  const [selectedOutput, setSelectedOutput] = useState<GenerationOutput | null>(null);
  const [lightboxId, setLightboxId] = useState<number | null>(null);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const [overrides, setOverrides] = useState<Record<number, Partial<GenerationOutput>>>({});
  const [retrying, setRetrying] = useState<number | null>(null);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [hoveredOutputId, setHoveredOutputId] = useState<number | null>(null);
  const [correctionOutput, setCorrectionOutput] = useState<GenerationOutput | null>(null);
  const [correctionSubmitting, setCorrectionSubmitting] = useState(false);
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [spotFixOutput, setSpotFixOutput] = useState<GenerationOutput | null>(null);
  const [spotFixSubmitting, setSpotFixSubmitting] = useState(false);
  const [spotFixError, setSpotFixError] = useState<string | null>(null);
  const [dynamicOutputs, setDynamicOutputs] = useState<GenerationOutput[]>([]);

  const baseOutputs = [...job.outputs, ...dynamicOutputs];

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const socketOutputs = useGenerationSocket(
    job.id,
    baseOutputs.map((o) => {
      const ov = overrides[o.id];
      return {
        output_id: o.id,
        mode: o.mode,
        status: ov?.status || o.status,
        image_url: ov?.image_url || o.image_url,
        prompt_used: ov?.prompt_used || o.prompt_used,
        error_message: ov?.error_message || o.error_message,
        fidelity_score: ov && "fidelity_score" in ov ? ov.fidelity_score : o.fidelity_score,
        marketing_score: ov && "marketing_score" in ov ? ov.marketing_score : o.marketing_score,
        qa_verdict: ov && "qa_verdict" in ov ? ov.qa_verdict : o.qa_verdict,
      };
    })
  );

  const liveOutputs: GenerationOutput[] = baseOutputs.map((o) => {
    const live = socketOutputs.find((s) => s.output_id === o.id);
    const base = { ...o, ...overrides[o.id] };
    if (!live) return base;
    const updated = { ...base, status: live.status, prompt_used: live.prompt_used, error_message: live.error_message };
    if (live.status === "done" && live.image_url) updated.image_url = live.image_url;
    updated.fidelity_score = live.fidelity_score ?? base.fidelity_score;
    updated.marketing_score = live.marketing_score ?? base.marketing_score;
    updated.qa_verdict = live.qa_verdict ?? base.qa_verdict;
    return updated;
  });

  const viewableOutputs = liveOutputs.filter((o) => o.status === "done" && o.image_url);
  const lightboxIndex = lightboxId === null ? -1 : viewableOutputs.findIndex((o) => o.id === lightboxId);

  const doneCount    = liveOutputs.filter((o) => o.status === "done").length;
  const runningCount = liveOutputs.filter((o) => o.status === "running").length;
  const allDone      = liveOutputs.length > 0 && liveOutputs.every((o) => o.status === "done" || o.status === "failed");
  const progress     = liveOutputs.length > 0 ? Math.round((doneCount / liveOutputs.length) * 100) : 0;

  const prevAllDone = useRef(allDone);
  useEffect(() => {
    if (allDone && !prevAllDone.current && document.hidden) {
      const fire = () => new Notification("AvantoAI", {
        body: t.completed(doneCount, liveOutputs.length),
        icon: "/favicon.ico",
      });
      if (Notification.permission === "granted") {
        fire();
      } else if (Notification.permission === "default") {
        Notification.requestPermission().then((p) => { if (p === "granted") fire(); });
      }
    }
    prevAllDone.current = allDone;
  }, [allDone]);

  const variantsSummary = product.variants_json?.length
    ? t.variants(product.variants_json.length)
    : null;
  const dimSummary = product.dimensions_json
    ? Object.entries(product.dimensions_json).map(([k, v]) => `${k}: ${v}`).join(" · ")
    : null;

  const handleDownloadAll = () => {
    window.location.href = `/products/${product.id}/generation_jobs/${job.id}/download`;
  };

  const retryOutput = async (output: GenerationOutput) => {
    if (retrying) return;
    setRetrying(output.id);
    setRetryError(null);
    setOverrides((prev) => ({
      ...prev,
      [output.id]: { status: "queued", image_url: undefined, error_message: undefined, prompt_used: undefined, fidelity_score: undefined, marketing_score: undefined, qa_verdict: undefined, qa_issues: undefined },
    }));

    try {
      await retryGenerationOutput(product.id, job.id, output.id);
      if (selectedOutput?.id === output.id) setSelectedOutput(null);
    } catch (err) {
      setRetryError(requestErrorMessage(err, c.errors.retryGenerationFailed));
      setOverrides((prev) => ({
        ...prev,
        [output.id]: { status: "failed", error_message: output.error_message },
      }));
    } finally {
      setRetrying(null);
    }
  };

  const regenerateOutput = async (output: GenerationOutput) => {
    if (retrying) return;
    setRetrying(output.id);
    setRetryError(null);
    setOverrides((prev) => ({
      ...prev,
      [output.id]: {
        status: "queued",
        image_url: undefined,
        error_message: undefined,
        prompt_used: output.prompt_used,
        fidelity_score: undefined,
        marketing_score: undefined,
        qa_verdict: undefined,
        qa_issues: undefined,
      },
    }));

    try {
      await regenerateGenerationOutput(product.id, job.id, output.id);
      if (selectedOutput?.id === output.id) setSelectedOutput(null);
    } catch (err) {
      setRetryError(requestErrorMessage(err, c.errors.regenerateGenerationFailed));
      setOverrides((prev) => ({
        ...prev,
        [output.id]: {
          status: output.status,
          image_url: output.image_url,
          prompt_used: output.prompt_used,
          error_message: output.error_message,
        },
      }));
    } finally {
      setRetrying(null);
    }
  };

  const openCorrection = (output: GenerationOutput) => {
    if (correctionSubmitting) return;

    setCorrectionError(null);
    setSelectedOutput(null);
    setLightboxId(null);
    setCorrectionOutput(output);
  };

  const submitCorrection = async (instruction: string) => {
    if (!correctionOutput || correctionSubmitting) return;

    setCorrectionSubmitting(true);
    setCorrectionError(null);
    try {
      const corrected = await correctGenerationOutput(product.id, job.id, correctionOutput.id, instruction);
      window.location.assign(`/products/${product.id}/generation_jobs/${corrected.job_id}`);
    } catch (error) {
      setCorrectionError(requestErrorMessage(error, c.errors.manualCorrectionFailed));
    } finally {
      setCorrectionSubmitting(false);
    }
  };

  const openSpotFix = (output: GenerationOutput) => {
    if (spotFixSubmitting) return;

    setSpotFixError(null);
    setSelectedOutput(null);
    setLightboxId(null);
    setSpotFixOutput(output);
  };

  const submitSpotFix = async (params: { referenceFileId: number; sourceBox: { x: number; y: number; w: number; h: number }; referenceBox: { x: number; y: number; w: number; h: number }; note: string }) => {
    if (!spotFixOutput || spotFixSubmitting) return;

    setSpotFixSubmitting(true);
    setSpotFixError(null);
    try {
      const created = await spotFixGenerationOutput(product.id, job.id, spotFixOutput.id, params);
      setDynamicOutputs((prev) => [
        ...prev,
        { id: created.output_id, mode: created.mode, status: created.status },
      ]);
      setSpotFixOutput(null);
    } catch (error) {
      setSpotFixError(requestErrorMessage(error, c.errors.spotFixFailed));
    } finally {
      setSpotFixSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)" }}>
      {/* ── Topbar ── */}
      <div className="v3-topbar">
        <a href="/app" className="v3-logo">
          <span className="v3-logo-mark" />
          AvantoAI
        </a>
        <div className="v3-topbar-grow" />
        <div className="v3-chips">
          <button className="v3-chip done" onClick={() => router.get(`/products/${product.id}`)}>
            <Chk /> <span>Upload</span>
          </button>
          <button className="v3-chip done" onClick={() => router.get(`/products/${product.id}`)}>
            <Chk /> <span>{t.product}</span>
          </button>
          <button className="v3-chip done" onClick={() => router.get(`/products/${product.id}`)}>
            <Chk /> <span>{t.visuals}</span>
          </button>
          <button className="v3-chip active">
            <span>{t.results}</span>
          </button>
        </div>
        <button className="v3-icon-btn" onClick={() => setTheme(theme === "dark" ? "light" : "dark")}>
          {theme === "dark" ? I.sun : I.moon}
        </button>
      </div>

      <div className="v3-page">

        {/* ── Section 1: Upload - done summary ── */}
        <div className="v3-sec">
          <button type="button" className="v3-sec-summary" onClick={() => router.get(`/products/${product.id}`)}>
            <span className="v3-sum-step mono">01 · UPLOAD</span>
            <div className="v3-sum-content">
              <span className="v3-sum-title">{t.filesUploaded}</span>
            </div>
            <span className="v3-sum-badge"><Chk /> gotowe</span>
          </button>
        </div>

        {/* ── Section 2: Product - done summary ── */}
        <div className="v3-sec">
          <button type="button" className="v3-sec-summary" onClick={() => router.get(`/products/${product.id}`)}>
            <span className="v3-sum-step mono">02 · {t.productStep}</span>
            <div className="v3-sum-content">
              <span className="v3-sum-title">{product.name}</span>
              {variantsSummary && <span className="v3-sum-desc">· {variantsSummary}</span>}
            </div>
            <span className="v3-sum-badge"><Chk /> gotowe</span>
          </button>
        </div>

        {/* ── Section 3: Modes - done summary ── */}
        <div className="v3-sec">
          <button type="button" className="v3-sec-summary" onClick={() => router.get(`/products/${product.id}`)}>
            <span className="v3-sum-step mono">03 · {t.visualsStep}</span>
            <div className="v3-sum-content">
              <span className="v3-sum-title">{t.selectedModes}</span>
              <div className="v3-sum-tags">
                {liveOutputs.map((o) => (
                  <span key={o.id} className="v3-sum-tag">{modeLabel(o.mode)}</span>
                ))}
              </div>
            </div>
            <span className="v3-sum-badge"><Chk /> {t.submitted}</span>
          </button>
        </div>

        {/* ── Section 4: Results ── */}
        <div className="v3-sec">
          <div className="v3-sec-open v3-slide-in">
            <div className="v3-sec-hd">
              <span className="v3-sec-num mono">{t.step} 04</span>
              <div>
                <h2>{t.generatingVisuals}</h2>
                <p>
                  {allDone
                    ? t.completed(doneCount, liveOutputs.length)
                    : t.readyOf(doneCount, liveOutputs.length, runningCount)}
                </p>
              </div>
              {allDone && (
                <button className="btn btn-primary btn-sm" style={{ marginLeft: "auto" }} onClick={handleDownloadAll}>
                  {I.download} {t.downloadZip}
                </button>
              )}
            </div>

            <div className="v3-sec-body">
              {retryError && <div className="v3-alert-error">{retryError}</div>}
              {/* Progress bar */}
              {!allDone && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ height: 4, background: "var(--bg-3)", borderRadius: 99, overflow: "hidden" }}>
                    <div style={{ height: "100%", background: "var(--accent)", borderRadius: 99, width: `${progress}%`, transition: "width .5s ease" }} />
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "var(--muted)" }}>
                    <span>{doneCount} / {liveOutputs.length} {t.readyLower}</span>
                    <span>{progress}%</span>
                  </div>
                </div>
              )}

              {/* Results grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
                {liveOutputs.map((o) => (
                  <div
                    key={o.id}
                    onClick={() => {
                      if (o.status === "done" && o.image_url) setLightboxId(o.id);
                      else if (o.prompt_used || o.error_message) setSelectedOutput(o);
                    }}
                    onMouseEnter={() => setHoveredOutputId(o.id)}
                    onMouseLeave={() => setHoveredOutputId(null)}
                    style={{
                      border: `1px solid ${selectedOutput?.id === o.id ? "var(--ink)" : "var(--line)"}`,
                      borderRadius: 8,
                      background: "var(--surface)",
                      overflow: "hidden",
                      cursor: o.status === "done" || o.prompt_used || o.error_message ? "pointer" : "default",
                      display: "flex",
                      flexDirection: "column",
                      boxShadow: selectedOutput?.id === o.id ? "0 0 0 2px var(--accent)" : undefined,
                      transition: "all .2s",
                    }}
                  >
                    {/* Thumb */}
                    <div style={{ aspectRatio: "1", background: "var(--bg-2)", position: "relative", overflow: "hidden" }}>
                      {o.status === "done" && o.image_url && (
                        <img src={o.image_url} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      )}
                      {o.status === "running" && (
                        <div style={{
                          position: "absolute", inset: 0,
                          background: "linear-gradient(90deg, transparent, color-mix(in oklab, var(--accent) 25%, transparent), transparent)",
                          animation: "gen-shimmer 1.6s ease-in-out infinite",
                        }} />
                      )}
                      <span style={{
                        position: "absolute", left: 7, top: 7,
                        fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
                        padding: "2px 5px", borderRadius: 3,
                        background: o.status === "done" ? "var(--accent)" : o.status === "running" ? "var(--ink)" : "var(--bg-3)",
                        color: o.status === "done" ? "var(--accent-ink)" : o.status === "running" ? "var(--bg)" : "var(--muted)",
                        border: "1px solid transparent",
                        letterSpacing: ".05em",
                      }}>
                        {o.status === "done" ? "DONE" : o.status === "running" ? "RENDERING" : o.status === "failed" ? "FAILED" : "QUEUED"}
                      </span>
                      {o.status === "running" && (
                        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 2, background: "var(--accent)", opacity: 0.7 }} />
                      )}
                      {o.status === "failed" && (
                        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#c0392b", fontSize: 22 }}>
                          ⚠
                        </div>
                      )}
                      {o.status === "done" && !!o.token_usage?.total_tokens && (
                        <span
                          title={t.usedTokens(formatTokensPl(o.token_usage.total_tokens))}
                          style={{
                            position: "absolute",
                            right: 7,
                            bottom: 7,
                            fontFamily: "'JetBrains Mono', monospace",
                            fontSize: 9,
                            padding: "2px 5px",
                            borderRadius: 3,
                            background: "oklch(1 0 0 / .92)",
                            color: "var(--muted)",
                            border: "1px solid oklch(0 0 0 / .1)",
                            letterSpacing: ".05em",
                          }}
                        >
                          {formatTokensCompact(o.token_usage.total_tokens)} {t.tokens}
                        </span>
                      )}
                      {o.status === "done" && o.image_url && hoveredOutputId === o.id && (
                        <button
                          type="button"
                          className="btn btn-icon btn-sm"
                          title={c.actions.correctGeneration}
                          aria-label={c.actions.correctGeneration}
                          disabled={correctionSubmitting}
                          onClick={(event) => {
                            event.stopPropagation();
                            openCorrection(o);
                          }}
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 42,
                            width: 28,
                            height: 28,
                            background: "oklch(1 0 0 / .92)",
                            borderColor: "oklch(0 0 0 / .12)",
                            boxShadow: "0 8px 20px oklch(0 0 0 / .18)",
                          }}
                        >
                          {I.edit}
                        </button>
                      )}
                      {o.status === "done" && o.image_url && hoveredOutputId === o.id && canSpotFix && (
                        <button
                          type="button"
                          className="btn btn-icon btn-sm"
                          title={c.actions.spotFix}
                          aria-label={c.actions.spotFix}
                          disabled={spotFixSubmitting}
                          onClick={(event) => {
                            event.stopPropagation();
                            openSpotFix(o);
                          }}
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 76,
                            width: 28,
                            height: 28,
                            background: "oklch(1 0 0 / .92)",
                            borderColor: "oklch(0 0 0 / .12)",
                            boxShadow: "0 8px 20px oklch(0 0 0 / .18)",
                          }}
                        >
                          {I.crop}
                        </button>
                      )}
                      {o.status === "done" && o.image_url && hoveredOutputId === o.id && (
                        <button
                          type="button"
                          className="btn btn-icon btn-sm"
                          title={c.actions.generateAgain}
                          aria-label={c.actions.generateAgain}
                          disabled={retrying === o.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            regenerateOutput(o);
                          }}
                          style={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            width: 28,
                            height: 28,
                            background: "oklch(1 0 0 / .92)",
                            borderColor: "oklch(0 0 0 / .12)",
                            boxShadow: "0 8px 20px oklch(0 0 0 / .18)",
                          }}
                        >
                          {I.refresh}
                        </button>
                      )}
                    </div>
                    {/* Meta */}
                    <div style={{ padding: "8px 10px", display: "flex", alignItems: "center", justifyContent: "space-between", borderTop: "1px solid var(--line)", gap: 8 }}>
                      <span style={{ fontSize: 11.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {modeLabel(o.mode)}
                      </span>
                      {o.status === "done" && o.image_url && (
                        <a href={o.image_url} download style={{ color: "var(--muted)", flexShrink: 0 }} title={c.actions.download}>
                          {I.download}
                        </a>
                      )}
                      {o.status === "failed" && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ height: 24, padding: "0 8px", fontSize: 11 }}
                          onClick={(event) => {
                            event.stopPropagation();
                            retryOutput(o);
                          }}
                          disabled={retrying === o.id}
                        >
                          {c.actions.retry}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Selected preview */}
              {selectedOutput && (
                <div style={{ marginTop: 24, border: "1px solid var(--line)", borderRadius: 8, overflow: "hidden", background: "var(--surface)" }}>
                  <div style={{ padding: "10px 14px", borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{modeLabel(selectedOutput.mode)}</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      {selectedOutput.image_url && <a href={selectedOutput.image_url} download className="btn btn-sm">{I.download} {c.actions.download}</a>}
                      {selectedOutput.status === "done" && selectedOutput.image_url && (
                        <button
                          className="btn btn-sm btn-ghost"
                          title={c.actions.correctGeneration}
                          aria-label={c.actions.correctGeneration}
                          disabled={correctionSubmitting}
                          onClick={() => openCorrection(selectedOutput)}
                        >
                          {I.edit}
                        </button>
                      )}
                      {selectedOutput.status === "done" && selectedOutput.image_url && canSpotFix && (
                        <button
                          className="btn btn-sm btn-ghost"
                          title={c.actions.spotFix}
                          aria-label={c.actions.spotFix}
                          disabled={spotFixSubmitting}
                          onClick={() => openSpotFix(selectedOutput)}
                        >
                          {I.crop}
                        </button>
                      )}
                      {selectedOutput.status === "done" && selectedOutput.image_url && (
                        <button
                          className="btn btn-sm btn-ghost"
                          title={c.actions.generateAgain}
                          aria-label={c.actions.generateAgain}
                          disabled={retrying === selectedOutput.id}
                          onClick={() => regenerateOutput(selectedOutput)}
                        >
                          {I.refresh}
                        </button>
                      )}
                      {selectedOutput.status === "failed" && (
                        <button className="btn btn-sm btn-ghost" onClick={() => retryOutput(selectedOutput)}>{c.actions.retry}</button>
                      )}
                      <button className="btn btn-sm btn-ghost" onClick={() => setSelectedOutput(null)}>{I.x}</button>
                    </div>
                  </div>
                  {selectedOutput.image_url && (
                    <img
                      src={selectedOutput.image_url}
                      style={{ width: "100%", display: "block", maxHeight: 640, objectFit: "contain", background: "var(--bg-2)" }}
                    />
                  )}
                  {!!selectedOutput.token_usage?.total_tokens && (
                    <div style={{ padding: "8px 14px", borderTop: "1px solid var(--line)", fontSize: 12, color: "var(--muted)", fontFamily: "'JetBrains Mono', monospace" }}>
                      {formatTokensPl(selectedOutput.token_usage.total_tokens)} {t.tokens}
                    </div>
                  )}
                  {selectedOutput.error_message && (
                    <div style={{ padding: 14, borderTop: "1px solid var(--line)", color: "#c0392b", fontSize: 12 }}>
                      {selectedOutput.error_message}
                    </div>
                  )}
                  {selectedOutput.prompt_used && current_user.role === "admin" && (
                    <details style={{ padding: 14, borderTop: "1px solid var(--line)" }}>
                      <summary style={{ cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.promptUsed}</summary>
                      <pre style={{ marginTop: 10, whiteSpace: "pre-wrap", fontSize: 11, lineHeight: 1.5, color: "var(--muted)", maxHeight: 360, overflow: "auto" }}>
                        {selectedOutput.prompt_used}
                      </pre>
                    </details>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
      {lightboxIndex >= 0 && (
        <OutputLightbox
          outputs={viewableOutputs}
          index={lightboxIndex}
          onIndexChange={(nextIndex) => {
            const next = viewableOutputs[nextIndex];
            if (next) setLightboxId(next.id);
          }}
          onClose={() => setLightboxId(null)}
          isAdmin={current_user.role === "admin"}
          onCorrect={openCorrection}
          correcting={correctionSubmitting}
          onSpotFix={canSpotFix ? openSpotFix : undefined}
          spotFixing={spotFixSubmitting}
        />
      )}
      {correctionOutput && (
        <ManualCorrectionDialog
          submitting={correctionSubmitting}
          error={correctionError}
          onClose={() => setCorrectionOutput(null)}
          onSubmit={submitCorrection}
        />
      )}
      {spotFixOutput && spotFixOutput.image_url && (
        <SpotFixDialog
          sourceImageUrl={spotFixOutput.image_url}
          referencePhotos={reference_photos}
          submitting={spotFixSubmitting}
          error={spotFixError}
          onClose={() => setSpotFixOutput(null)}
          onSubmit={submitSpotFix}
        />
      )}
    </div>
  );
}
